import React, { useState, useEffect } from 'react';
import { Users, Mail, Building, Clock, ExternalLink } from 'lucide-react';
import api from '../services/api';

const PilotRequestsAdmin = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchRequests(); }, []);

  const fetchRequests = async () => {
    try {
      const resp = await api.get('/pilots/requests');
      setRequests(resp.data);
    } catch (err) {
      console.error('Failed to load pilot requests', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-[#9CA3AF]">Loading requests...</div></div>;
  }

  return (
    <div data-testid="pilot-requests-admin" className="space-y-6">
      <div>
        <div className="flex items-center gap-3 mb-1">
          <Users className="w-6 h-6 text-[#0066FF]" />
          <h1 className="text-2xl font-bold text-[#F9FAFB]" style={{ fontFamily: 'Chivo, sans-serif' }}>Pilot Requests</h1>
        </div>
        <p className="text-sm text-[#6B7280]">Inbound leads from the Request a Pilot form</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="enterprise-card p-4">
          <div className="text-2xl font-bold font-mono text-[#0066FF]">{requests.length}</div>
          <div className="text-[10px] text-[#6B7280] uppercase tracking-wider">Total Requests</div>
        </div>
        <div className="enterprise-card p-4">
          <div className="text-2xl font-bold font-mono text-[#34C759]">{requests.filter(r => r.status === 'new').length}</div>
          <div className="text-[10px] text-[#6B7280] uppercase tracking-wider">New</div>
        </div>
        <div className="enterprise-card p-4">
          <div className="text-2xl font-bold font-mono text-[#FFCC00]">{requests.filter(r => r.status === 'contacted').length}</div>
          <div className="text-[10px] text-[#6B7280] uppercase tracking-wider">Contacted</div>
        </div>
        <div className="enterprise-card p-4">
          <div className="text-2xl font-bold font-mono text-[#F9FAFB]">{[...new Set(requests.map(r => r.company))].length}</div>
          <div className="text-[10px] text-[#6B7280] uppercase tracking-wider">Companies</div>
        </div>
      </div>

      {/* Table */}
      {requests.length === 0 ? (
        <div className="enterprise-card p-12 text-center">
          <Mail className="w-12 h-12 mx-auto mb-4 text-[#27272A]" />
          <p className="text-[#6B7280]">No pilot requests yet</p>
          <p className="text-xs text-[#6B7280] mt-1">Requests submitted via /request-pilot will appear here</p>
        </div>
      ) : (
        <div className="enterprise-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-[#1A1A1A] border-b border-[#27272A]/40">
                <th className="text-left p-4 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Contact</th>
                <th className="text-left p-4 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Company</th>
                <th className="text-left p-4 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Role</th>
                <th className="text-left p-4 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">NHI Count</th>
                <th className="text-left p-4 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Deployment</th>
                <th className="text-left p-4 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Date</th>
                <th className="text-left p-4 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#27272A]/30">
              {requests.map((r) => (
                <tr key={r.id} className="hover:bg-[#1A1A1A]/30 transition-colors" data-testid={`request-${r.id}`}>
                  <td className="p-4">
                    <div className="text-sm text-[#F9FAFB] font-medium">{r.full_name}</div>
                    <div className="text-xs text-[#0066FF]">{r.email}</div>
                  </td>
                  <td className="p-4">
                    <div className="text-sm text-[#F9FAFB]">{r.company}</div>
                  </td>
                  <td className="p-4 text-sm text-[#9CA3AF]">{r.role}</td>
                  <td className="p-4 text-sm font-mono text-[#F9FAFB]">{r.nhi_count}</td>
                  <td className="p-4 text-sm text-[#9CA3AF]">{r.deployment_preference}</td>
                  <td className="p-4 text-xs font-mono text-[#6B7280]">{formatDate(r.created_at)}</td>
                  <td className="p-4">
                    <span className={`text-[10px] font-mono uppercase px-2 py-0.5 ${
                      r.status === 'new' ? 'text-[#34C759] bg-[#34C759]/10' :
                      r.status === 'contacted' ? 'text-[#FFCC00] bg-[#FFCC00]/10' :
                      'text-[#9CA3AF] bg-[#9CA3AF]/10'
                    }`}>
                      {r.status || 'new'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Info */}
      <div className="enterprise-card p-4 border-l-4 border-[#0066FF]">
        <div className="text-xs text-[#9CA3AF]">
          <strong className="text-[#F9FAFB]">How it works:</strong> When someone submits the pilot request form at <span className="font-mono text-[#0066FF]">/request-pilot</span>, 
          it saves here and attempts to send a confirmation email to the lead and a notification to <span className="font-mono">{'{NOTIFICATION_EMAIL}'}</span>. 
          Email delivery requires a verified domain in Resend.
        </div>
      </div>
    </div>
  );
};

export default PilotRequestsAdmin;
